import React, { createContext, useContext, useState, useEffect } from 'react';

const MarketDataContext = createContext();

export const useMarketData = () => {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketData must be used within a MarketDataProvider');
  }
  return context;
};

export const MarketDataProvider = ({ children }) => {
  const [marketData, setMarketData] = useState({});
  const [marketSummary, setMarketSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketData();
    fetchMarketSummary();

    // Update market data every 5 seconds
    const interval = setInterval(() => {
      fetchMarketData();
    }, 5000);

    // Update market summary every 30 seconds
    const summaryInterval = setInterval(() => {
      fetchMarketSummary();
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(summaryInterval);
    };
  }, []);

  const fetchMarketData = async () => {
    try {
      const response = await fetch('/api/trading/market-data');
      if (response.ok) {
        const data = await response.json();
        setMarketData(data || {});
      } else {
        console.warn('Market data API returned non-OK status:', response.status);
        // Set default market data if API fails
        setMarketData({
          AAPL: { symbol: 'AAPL', price: 175, changePercent: 0, volume: 1000000, volatility: 0.02 },
          GOOGL: { symbol: 'GOOGL', price: 135, changePercent: 0, volume: 1000000, volatility: 0.02 },
          TSLA: { symbol: 'TSLA', price: 250, changePercent: 0, volume: 1000000, volatility: 0.03 }
        });
      }
    } catch (error) {
      console.error('Failed to fetch market data:', error);
      // Set default market data on error
      setMarketData({
        AAPL: { symbol: 'AAPL', price: 175, changePercent: 0, volume: 1000000, volatility: 0.02 },
        GOOGL: { symbol: 'GOOGL', price: 135, changePercent: 0, volume: 1000000, volatility: 0.02 },
        TSLA: { symbol: 'TSLA', price: 250, changePercent: 0, volume: 1000000, volatility: 0.03 }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketSummary = async () => {
    try {
      const response = await fetch('/api/trading/market-summary');
      if (response.ok) {
        const summary = await response.json();
        setMarketSummary(summary);
      }
    } catch (error) {
      console.error('Failed to fetch market summary:', error);
    }
  };

  const value = {
    marketData,
    marketSummary,
    loading,
    refreshMarketData: fetchMarketData
  };

  return (
    <MarketDataContext.Provider value={value}>
      {children}
    </MarketDataContext.Provider>
  );
};